import { createAsyncThunk } from '@reduxjs/toolkit';
import { errorGlobal, successGlobal } from '../reducers/notifications';
import { getAuthHeader } from '../../utils/tools';
import { FACILITY_TYPES } from '../../utils/constants/facilityTypes';
import { SHUTDOWN_STATUSES } from '../../utils/constants/shutdownStatuses';
import { LIMITS_COMPLIANCE } from '../../utils/constants/limitsCompliance';
import axios from 'axios';

axios.defaults.headers.post['Content-Type'] = 'application/json';

const buildEntryPayload = (values, facilityType) => {
    const payload = {
        date: new Date(values.date).toISOString(),
        responsiblePersons: values.responsiblePersons ?? [],
        wasteReceived: (values.wasteReceived ?? []).map(w => ({
            wasteCode: w.wasteCode,
            amount: w.amount,
            unit: w.unit,
            loadingCode: w.loadingCode,
            producerOrHolder: w.producerOrHolder
        })),
        wasteProcessed: (values.wasteProcessed ?? []).map(w => ({
            wasteCode: w.wasteCode,
            amount: w.amount,
            unit: w.unit,
            loadingCode: w.loadingCode
        })),
        wasteRejected: (values.wasteRejected ?? []).map(w => ({
            wasteCode: w.wasteCode,
            amount: w.amount,
            reason: w.reason
        })),
        solidLiquidWasteHandling: values.solidLiquidWasteHandling ?? '',
        samples: values.samples ?? [],
        technicalCondition: values.technicalCondition ?? '',
        malfunctions: values.malfunctions ?? [],
        operatingHours: values.operatingHours ?? '',
        shutdown: values.shutdown ?? { status: SHUTDOWN_STATUSES.NONE, reason: '' },
        maintenance: values.maintenance ?? [],
        authorityInspections: values.authorityInspections ?? [],
        limitsCompliance: values.limitsCompliance ?? LIMITS_COMPLIANCE.COMPLIANT,
        otherActivities: values.otherActivities ?? '',
    };
    if (facilityType === FACILITY_TYPES.SKLADKA) {
        payload.landfillDetails = values.landfillDetails ?? {};
    }
    if (facilityType === FACILITY_TYPES.ORTUT) {
        payload.mercuryReceived = (values.mercuryReceived ?? []).map(m => ({
            amount: m.amount,
            producerOrHolder: m.producerOrHolder,
            containerCertificateNumber: m.containerCertificateNumber
        }));
        payload.mercuryDispatched = (values.mercuryDispatched ?? []).map(m => ({
            amount: m.amount,
            recipient: m.recipient,
            treatmentFacility: m.treatmentFacility,
            permanentStorageFacility: m.permanentStorageFacility
        }));
    }
    return payload;
};

export const fetchEntries = createAsyncThunk(
    'operationalLogEntry/fetchEntries',
    async({ operationalLogId, year, month }, { dispatch }) => {
        try {
            const request = await axios.get(`/api/operationallog/${operationalLogId}/entry/fetch`, {
                ...getAuthHeader(),
                params: { year, month }
            });
            return request.data;
        } catch(error) {
            dispatch(errorGlobal(error.response?.data?.message || 'Nastala chyba. Skúste to prosím znova.'));
            throw error;
        }
    }
);

export const addEntry = createAsyncThunk(
    'operationalLogEntry/addEntry',
    async({ operationalLogId, values, facilityType }, { dispatch }) => {
        try {
            const payload = buildEntryPayload(values, facilityType);
            const request = await axios.post(`/api/operationallog/${operationalLogId}/entry/create`, payload, getAuthHeader());

            dispatch(successGlobal('Denný záznam bol úspešne vytvorený.'));
            return request.data;
        } catch(error) {
            dispatch(errorGlobal(error.response?.data?.message ?? 'Záznam sa nepodarilo vytvoriť.'));
            throw error;
        }
    }
);

export const getEntryById = createAsyncThunk(
    'operationalLogEntry/getEntryById',
    async({ operationalLogId, entryId }, { dispatch }) => {
        try {
            const request = await axios.get(`/api/operationallog/${operationalLogId}/entry/${entryId}`, getAuthHeader());
            return request.data;
        } catch(error) {
            dispatch(errorGlobal('Záznam sa nepodarilo načítať.'));
            throw error;
        }
    }
);

export const editEntry = createAsyncThunk(
    'operationalLogEntry/editEntry',
    async({ operationalLogId, entryId, values, facilityType }, { dispatch }) => {
        try {
            const payload = buildEntryPayload(values, facilityType);
            const request = await axios.patch(`/api/operationallog/${operationalLogId}/entry/${entryId}/edit`, payload, getAuthHeader());

            dispatch(successGlobal('Denný záznam bol úspešne upravený.'));
            return request.data;
        } catch(error) {
            dispatch(errorGlobal(error.response?.data?.message ?? 'Záznam sa nepodarilo upraviť.'));
            throw error;
        }
    }
);

export const deleteEntry = createAsyncThunk(
    'operationalLogEntry/deleteEntry',
    async({ operationalLogId, entryId }, { dispatch }) => {
        try {
            const request = await axios.delete(`/api/operationallog/${operationalLogId}/entry/${entryId}/delete`, getAuthHeader());

            dispatch(successGlobal('Denný záznam bol úspešne vymazaný.'));
            return request.data;
        } catch(error) {
            dispatch(errorGlobal(error.response?.data?.message ?? 'Záznam sa nepodarilo vymazať.'));
            throw error;
        }
    }
);
